import { makeAutoObservable } from 'mobx';
import IGeoCoordinate from '../interfaces/IGeoCoordinate';
import ILeaseComp, { ILeaseCompState } from '../interfaces/ILeaseComp';
import ILoadingProps, { ILoadingPropsDispatcher } from '../interfaces/ILoadingProps';
import LeaseCompsApiService from '../services/leaseCompsApiService';

export default class LeaseCompStore {
    leaseComps: ILeaseComp[] = [];
    isLeaseCompsLoading: ILoadingProps = {};
    editedLeaseCompId: number | null = null;
    radiusLeaseCompIds: number[] = [];

    constructor(
        private leaseCompsApiService: LeaseCompsApiService,
    ) {
        makeAutoObservable(this);
    }

    setLeaseComps(value: ILeaseComp[]) {
        this.leaseComps = value;
    }

    setIsLeaseCompsLoading(): ILoadingPropsDispatcher {
        return {
            fetching: (value: boolean) => this.isLeaseCompsLoading.fetching = value,
            creating: (value: boolean) => this.isLeaseCompsLoading.creating = value,
            updating: (value: boolean) => this.isLeaseCompsLoading.updating = value,
            deleting: (value: boolean) => this.isLeaseCompsLoading.deleting = value,
        };
    }

    setEditedLeaseCompId(value: number | null) {
        this.editedLeaseCompId = value;
    }

    setRadiusLeaseCompIds(value: number[]) {
        this.radiusLeaseCompIds = value;
    }

    initialize() {
        this.setIsLeaseCompsLoading().fetching(true);
        this.leaseCompsApiService.getLeaseComps().then(leaseComps => {
            this.setLeaseComps(leaseComps);
        }).finally(() => {
            this.setIsLeaseCompsLoading().fetching(false);
        });
    }

    async createLeaseComp(leaseCompState: ILeaseCompState, pipedriveCompanyId: number | null, centerPoint: IGeoCoordinate, regridId: string | null): Promise<number> {
        this.setIsLeaseCompsLoading().creating(true);
        try {
            const response = await this.leaseCompsApiService.createLeaseComp(leaseCompState, pipedriveCompanyId, centerPoint, regridId);

            this.setLeaseComps([
                ...this.leaseComps,
                {
                    ...leaseCompState,
                    address: leaseCompState.address!,
                    regridId: regridId,
                    totalRent: leaseCompState.totalRent!,
                    id: response.id,
                    pipedriveCompanyId: pipedriveCompanyId,
                    centerPoint: centerPoint,
                    modifiedByUser: response.modifiedByUser,
                    modifiedDate: response.modifiedDate,
                    attachments: [],
                },
            ]);

            return response.id;
        } finally {
            this.setIsLeaseCompsLoading().creating(false);
        }
    }

    async updateLeaseComp(leaseComp: ILeaseComp) {
        this.setIsLeaseCompsLoading().updating(true);
        try {
            const response = await this.leaseCompsApiService.updateLeaseComp(leaseComp);
            leaseComp.modifiedDate = response.modifiedDate;
            leaseComp.modifiedByUser = response.modifiedByUser;

            this.setLeaseComps([
                ...this.leaseComps.filter(lc => lc.id !== leaseComp.id),
                leaseComp,
            ]);
        } finally {
            this.setIsLeaseCompsLoading().updating(false);
        }
    }

    async deleteLeaseComp(id: number) {
        this.setIsLeaseCompsLoading().deleting(true);
        try {
            await this.leaseCompsApiService.deleteLeaseComp(id);

            this.setLeaseComps([
                ...this.leaseComps.filter(lc => lc.id !== id),
            ]);
        } finally {
            this.setIsLeaseCompsLoading().deleting(false);
        }
    }

    async uploadAttachment(leaseCompId: number, file: File): Promise<void> {
        const attachment = await this.leaseCompsApiService.uploadLeaseCompAttachment(leaseCompId, file);
        this.leaseComps.find(lc => lc.id === leaseCompId)!.attachments.push(attachment);
    }

    async deleteAttachment(leaseCompId: number, key: string): Promise<void> {
        await this.leaseCompsApiService.deleteLeaseCompAttachment(leaseCompId, key);
        const leaseComp = this.leaseComps.find(lc => lc.id === leaseCompId)!;
        leaseComp.attachments = leaseComp.attachments.filter(a => a.key !== key);
    }

    clear() {
        this.setLeaseComps([]);
        this.setEditedLeaseCompId(null);
        this.isLeaseCompsLoading = {};
    }
}